import type { Unstable_DirectiveFormatter } from '@assistant-ui/core';

/**
 * The agents and skills picked in Clu's composer. A pick goes
 * into the message text as a token, the way a list does (`@[label](listId)`):
 *
 *   an agent    `@[Scout](agent:<id>)`
 *   a skill     `/[ICP scoring](skill:<id>)`, one of the org's own skills
 *
 * The composer renders each token as a chip, the sent message shows it as a
 * chip, and the turn carries the ids in its context (`withMentionIds`), which
 * the API resolves in the caller's organization.
 */

/** The mention types the composer's chips and the menus use. */
export const AGENT_MENTION = 'agent';
export const SKILL_MENTION = 'user_skill';

const UUID =
  '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';
const TOKEN_SOURCE =
  `@\\[([^\\]\\n]+)\\]\\(agent:(${UUID})\\)` +
  `|/\\[([^\\]\\n]+)\\]\\(skill:(${UUID})\\)`;
const UUID_ONLY = new RegExp(`^${UUID}$`);

export type TokenMention = {
  kind: 'agent' | 'skill';
  id: string;
  label: string;
};

type Part = { kind: 'text'; text: string } | TokenMention;

/** A name as a token can hold it: no brackets, no line breaks. */
function tokenLabel(label: string): string {
  return (
    label
      .replace(/[[\]\n\r]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim() || 'Untitled'
  );
}

export function agentToken(label: string, id: string): string {
  return `@[${tokenLabel(label)}](agent:${id})`;
}

/** A list's token. A list name with brackets ("[bughunt] list") broke the
 *  token, so the composer showed it as raw text instead of a chip. */
export function listToken(label: string, id: string): string {
  return `@[${tokenLabel(label)}](${id})`;
}

export function skillToken(label: string, id: string): string {
  return `/[${tokenLabel(label)}](skill:${id})`;
}

export function isMentionId(id: string): boolean {
  return UUID_ONLY.test(id);
}

/** The text and the agent and skill tokens in it, in order. */
export function splitTokens(text: string): Part[] {
  const parts: Part[] = [];
  const pattern = new RegExp(TOKEN_SOURCE, 'g');
  let last = 0;
  for (const match of text.matchAll(pattern)) {
    const index = match.index ?? 0;
    if (index > last)
      parts.push({ kind: 'text', text: text.slice(last, index) });
    const [full, agentLabel, agentId, skillLabel, skillId] = match;
    parts.push(
      agentId
        ? { kind: 'agent', id: agentId, label: agentLabel }
        : { kind: 'skill', id: skillId, label: skillLabel }
    );
    last = index + full.length;
  }
  if (last < text.length) parts.push({ kind: 'text', text: text.slice(last) });
  return parts;
}

const NAMED_SOURCE =
  `/\\[([^\\]\\n]+)\\]\\(skill:${UUID}\\)` +
  `|@\\[([^\\]\\n]+)\\]\\([^)\\s]+\\)`;

/**
 * The text with each pick as its name: a skill's token, an agent's, and a
 * list's (`@[label](id)`). What a chat's title shows, as the server titles it.
 */
export function tokensAsNames(text: string): string {
  return text.replace(
    new RegExp(NAMED_SOURCE, 'g'),
    (_token, skill?: string, name?: string) => skill ?? name ?? ''
  );
}

/**
 * The text without its agent and skill tokens, and the agents and skills it
 * names, each once, in order. Text with no token comes back as it was.
 */
export function stripTokens(text: string): {
  text: string;
  agents: TokenMention[];
  skills: TokenMention[];
} {
  const parts = splitTokens(text);
  const agents: TokenMention[] = [];
  const skills: TokenMention[] = [];
  if (parts.every((part) => part.kind === 'text')) {
    return { text, agents, skills };
  }
  const seen = new Set<string>();
  let rest = '';
  for (const part of parts) {
    if (part.kind === 'text') {
      rest += part.text;
      continue;
    }
    const key = `${part.kind}:${part.id.toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    (part.kind === 'agent' ? agents : skills).push(part);
  }
  return {
    text: rest
      .replace(/[ \t]{2,}/g, ' ')
      .replace(/[ \t]+\n/g, '\n')
      .trim(),
    agents,
    skills,
  };
}

/**
 * The context a turn is sent with: the ids of the agents and skills the
 * message picked, beside the chat's own context. A message that picks none
 * sends the context as it was.
 */
export type MentionIds = {
  mentioned_agent_ids?: string[];
  mentioned_skill_ids?: string[];
};

export function withMentionIds<C extends object>(
  context: C,
  message: string
): C & MentionIds {
  const { agents, skills } = stripTokens(message);
  if (agents.length === 0 && skills.length === 0)
    return context as C & MentionIds;
  return {
    ...context,
    ...(agents.length > 0
      ? { mentioned_agent_ids: agents.map((agent) => agent.id) }
      : {}),
    ...(skills.length > 0
      ? { mentioned_skill_ids: skills.map((skill) => skill.id) }
      : {}),
  };
}

/**
 * The composer's formatter with agent and skill chips: their tokens become
 * chips, and everything else goes to the composer's own formatter (lists and
 * Clu's commands) as before.
 */
export function withCluMentions(
  base: Unstable_DirectiveFormatter
): Unstable_DirectiveFormatter {
  return {
    serialize: (item) => {
      if (item.type === AGENT_MENTION && isMentionId(item.id)) {
        return agentToken(item.label, item.id);
      }
      if (item.type === SKILL_MENTION && isMentionId(item.id)) {
        return skillToken(item.label, item.id);
      }
      return base.serialize(item);
    },
    parse: (text) => {
      const parts = splitTokens(text);
      if (parts.every((part) => part.kind === 'text')) return base.parse(text);
      return parts.flatMap((part) =>
        part.kind === 'text'
          ? base.parse(part.text)
          : [
              {
                kind: 'mention' as const,
                id: part.id,
                type: part.kind === 'agent' ? AGENT_MENTION : SKILL_MENTION,
                label: part.label,
              },
            ]
      );
    },
  };
}

/**
 * Where a composer menu opens: above the composer at the thread's foot, and
 * below it in an empty chat's welcome (`below`), where only the hello is
 * above and a menu opening up would run past the top of the scrolling
 * thread, out of reach.
 */
export const composerMenuPlace = (below?: boolean) =>
  below
    ? 'absolute top-full right-0 left-0 z-20 mt-2'
    : 'absolute right-0 bottom-full left-0 z-20 mb-2';
